import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {flatten, get, head, isNull, isString, map, some, values} from "lodash";
import moment from "moment";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {slotDayName, slotKey, slotTimeLabel} from "business/callSlots";
import TimeSelection from "components/TimeSelection";
import {color, font, radius, type} from "common/tokens";
import CtaButton from "./CtaButton";
import Dialog from "./Dialog";
import IstNote from "./IstNote";

const useStyles = makeStyles({
  label: {
    ...type.eyebrow,
    fontWeight: font.BOLD,
    color: color.ACCENT_TEXT,
    margin: '24px 0 10px',
  },
  firstLabel: {
    marginTop: 0,
  },
  days: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  day: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    appearance: 'none',
    border: 'none',
    padding: '7px 12px',
    borderRadius: radius.CONTROL,
    background: color.SURFACE_MUTED,
    color: color.TEXT_MUTED,
    cursor: 'pointer',
  },
  daySelected: {
    background: color.ACCENT,
    color: color.ON_ACCENT,
    fontWeight: font.BOLD,
  },
  note: {
    marginTop: 8,
  },
  summary: {
    ...type.body,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '20px 0 0',
    padding: '12px 16px',
    borderRadius: radius.CONTROL,
    background: color.ACCENT_TINT,
    color: color.TEXT,
    fontWeight: font.BOLD,
  },
  summaryLabel: {
    ...type.eyebrow,
    fontWeight: font.BOLD,
    color: color.ACCENT_TEXT,
  },
  error: {
    ...type.meta,
    color: color.DANGER,
    margin: '12px 0 0',
  },
  add: {
    background: color.TEXT,
    color: color.SURFACE,
    boxShadow: 'none',
    textTransform: 'uppercase',
  },
});

const AddSlotDialog = ({weekdays, existingSlots, onClose, onAdd}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [weekday, setWeekday] = useState(null);
  const [time, setTime] = useState(() => moment().hour(19).minute(0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pickedWeekday = isNull(weekday) ? head(weekdays) : weekday;
  const slot = time && {weekday: pickedWeekday, hour: time.hour(), minute: time.minute()};
  const alreadyListed = Boolean(slot) && some(existingSlots, existing => slotKey(existing) === slotKey(slot));

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await onAdd(slot);
      onClose();
    } catch (e) {
      const message = head(flatten(values(get(e, 'response.data'))));
      setError(isString(message) ? message : t('slotUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      title={t('addASlot')}
      subtitle={t('addSlotHint')}
      prominentTitle
      maxWidth={480}
      footer={(
        <CtaButton
          fullWidth
          size={'large'}
          className={classes.add}
          label={t('addThisSlot')}
          disabled={!slot || alreadyListed}
          loading={saving}
          onClick={add}/>
      )}>
      <p className={classNames(classes.label, classes.firstLabel)}>{t('day')}</p>
      <div className={classes.days}>
        {map(weekdays, day => (
          <button
            type={'button'}
            key={day}
            aria-pressed={day === pickedWeekday}
            className={classNames(classes.day, day === pickedWeekday && classes.daySelected)}
            onClick={() => setWeekday(day)}>
            {slotDayName(day, 'ddd')}
          </button>
        ))}
      </div>

      <p className={classes.label}>{t('time')}</p>
      <TimeSelection value={time} onAnswered={setTime} showBorder/>
      <IstNote className={classes.note}/>

      {Boolean(slot) && (
        <div className={classes.summary}>
          <span className={classes.summaryLabel}>{t('selected')}</span>
          {`${slotDayName(slot.weekday)}, ${slotTimeLabel(slot.hour, slot.minute)}`}
        </div>
      )}

      {alreadyListed && <p className={classes.error}>{t('slotAlreadyAdded')}</p>}
      {error && <p className={classes.error}>{error}</p>}
    </Dialog>
  );
};

export default AddSlotDialog;
